import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LabelList } from "recharts"
import { DisaggregatedMetric, GeoDetails, GeoTag } from "../../services/types"
import {
    BarValueLabel,
	BarPercentageLabel,
    CustomTick,
    CustomTooltipBar,
} from "../../utils/rechartsComponents";
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import { formatValue, formatValueBase } from "../../utils/helpers";
import { indexMetricIds } from "../../utils/dashboardUtils";
import { useDashboardColor } from '../../context/DashboardColorContext';
import MetricMetadataTrigger from "../metrics/MetricMetadataTrigger";

interface BarChartOnlyProps {
    metric: DisaggregatedMetric | undefined;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    barColor?: string;
    height: string;
    background: string;
    showMetadataIcon?: boolean;
    topFive?: boolean;
    subText?: string;
    showValue?: boolean;
    labelOverride?: number;
}

const normalizeLabel = (value: string) =>
	value
		.replace(/\u00A0/g, " ")   // NBSP → space
		.replace(/\s+/g, " ")      // collapse whitespace
		.replace(/\bNH\b/g, "Non-Hispanic")
		.trim();

const MetricBarChart = ({
    metric,
    disaggData,
    geoDetails,
    geoTag,
    selectionActive,
    height,
    background,
    barColor = "#3B5C67",
    showMetadataIcon = true,
    topFive = false,
    subText,
    showValue = false,
    labelOverride
}: BarChartOnlyProps) => {
	const color = useDashboardColor();
	const yAxisWidth = labelOverride ?? 100;
	const maxLineLength = labelOverride ? Math.floor(labelOverride / 5.5) : 20;

	// Wait for metric and data to be available
	if (!metric || !metric.versions || metric.versions.length === 0 || !metric.versions[0].disaggregations) {
		return <div className="h-24 flex items-center justify-center text-xs text-slate-500"><Skeleton height={24} width={"100%"} /></div>;
	}

	const id = metric.id;
	const name = metric.name;

	// Index metric?
	const isIndexMetric = indexMetricIds.includes(metric.id);

	const versions = metric?.versions || [];
	const disaggregations = versions[0].disaggregations;
	const unit = versions[0]?.unit;
	const value = versions[0]?.value;

	console.log('disaggregations for chart:', disaggregations);

	// Sort by highest value (treat missing values as 0)
	const sorted = [...disaggregations].sort(
		(a, b) => (b.value ?? 0) - (a.value ?? 0)
	);

	const total = disaggregations.reduce(
		(sum, d) => sum + Number(d.value ?? 0),
		0
	);

	// Only keep top 5 if prop is true
	const filtered = topFive ? sorted.slice(0, 5) : sorted;

	const chartData = filtered.map(d => ({
		name: d.name,
		value: d.value ?? 0,
		unit,
	}));

	if (chartData.length === 0) {
		return <div className="h-24 flex items-center justify-center text-xs text-slate-500">No data available</div>
	}

	return (
		<>
			<div
				className="relative overflow-hidden group hover:shadow-md transition-all border rounded-xl bg-white"
				style={{
					borderLeftWidth: isIndexMetric ? "4px" : undefined, borderLeftColor: isIndexMetric ? color : undefined,
				}}
			>
				<div className="p-3 bg-white">
					<div className="flex items-center justify-between">
						<div>
							<h3 className="text-sm font-semibold text-port-text">
								{name}
							</h3>
							<div className="flex items-center">
								<p className="text-port-text text-xs mr-2">{subText}</p>
								<p className="text-port-text text-xs font-bold">
									{showValue && value && unit && (
										formatValueBase(value, unit)
									)}
								</p>
							</div>
							{isIndexMetric &&
								<div className="mt-0.5 rounded-lg h-4 w-10 px-1.5 text-[10px] text-white text-center" style={{ backgroundColor: color }}>Index</div>
							}
						</div>

						<MetricMetadataTrigger
							metricId={id}
							metricName={name}
							disaggData={disaggData}
							geoDetails={geoDetails}
							geoTag={geoTag}
							selectionActive={selectionActive}
							showIcon={showMetadataIcon}
						/>
					</div>
				</div>

				<div className={`${height} ${background} min-h-[120px] min-w-0`}>
					<ResponsiveContainer width="100%" height="100%" minHeight={120}>
						<BarChart
							data={chartData}
							layout="vertical"
							margin={{ top: 2, right: 40, left: 15, bottom: 0 }}
							barCategoryGap="8%"
							barGap={-2}
						>
							<XAxis
								type="number"
								tick={{ fontSize: 10 }}
								tickFormatter={(value) => formatValue(value, unit, metric.id)}
							/>
							<YAxis
								type="category"
								dataKey="name"
								tick={<CustomTick maxLineLength={maxLineLength} />}
								width={yAxisWidth}
								interval={0}
								tickLine={false}
							/>
							<Tooltip
								content={
									<CustomTooltipBar
										valueLabel="Value"
										total={total}
										valueFormatter={(value) =>
											formatValue(value, unit, metric.id)
										}
										labelFormatter={normalizeLabel}
									/>
								}
								cursor={{ fill: "transparent" }}
							/>
														<Bar
								dataKey="value"
								fill={barColor}
								activeBar={false}
								radius={[0, 4, 4, 0]}
								barSize={14}
								isAnimationActive={false}
							>
								<LabelList
									dataKey="value"
									content={(props) => (
										//@ts-ignore
										<BarPercentageLabel
											{...props}
											total={total}
										/>
									)}
								/>

								<LabelList
									dataKey="value"
									content={(props) => (
										//@ts-ignore
										<BarValueLabel
											{...props}
											formatter={(value) =>
												formatValue(value, unit, metric.id)
											}
										/>
									)}
								/>
							</Bar>
						</BarChart>
					</ResponsiveContainer>
				</div>

				<div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2258e5]/50 to-[#00ac4e]/50 opacity-0 group-hover:opacity-100 transition-opacity" />
			</div>
		</>
	)
}

export default MetricBarChart;